import { Checkbox } from '@myosh/odin-components';
import { type Ref, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { toast } from 'react-toastify';
import {
  useDeleteGlobalHierarchyFiltersMutation,
  useGetGlobalHierarchyFiltersQuery,
  useHierarchyTypesQuery,
  useUpdateGlobalHierarchyFiltersMutation,
} from '../../../api/enhanced/enhanced-v4-api';
import { buildGlobalHierarchyFiltersBody, groupSelectedByType } from '../../../helpers/global-hierarchy';
import HierarchyTypeMultiSelect from './hierarchy-type-multi-select.component';

interface HierarchyFilterTabProps {
  ref?: Ref<HierarchyFilterTabRef>;
}

export interface HierarchyFilterTabRef {
  save: () => void;
}

/**
 * Editable global-hierarchy selector. Lists each hierarchy type as a multi-select, lets the user
 * choose match any/all, and persists the scope to `/global-hierarchy-filters` on save — which the
 * record picker reads to scope its list. Mirrors the web app's global hierarchy filter.
 */
function HierarchyFilterTab({ ref }: HierarchyFilterTabProps) {
  const { data: types = [] } = useHierarchyTypesQuery();
  const { data: savedScope } = useGetGlobalHierarchyFiltersQuery();

  const [updateFilters] = useUpdateGlobalHierarchyFiltersMutation();
  const [deleteFilters] = useDeleteGlobalHierarchyFiltersMutation();

  const [selectedByType, setSelectedByType] = useState<Record<number, number[]>>({});
  const [matchAll, setMatchAll] = useState(false);
  // Whether the user has edited the scope this session. Apply only persists real edits, which also
  // prevents a cold-load race (Apply fired before the saved scope hydrates) from clearing the scope.
  const [isDirty, setIsDirty] = useState(false);

  // Hydrate the working copy when the saved scope arrives, and treat it as a clean (unedited) baseline.
  // Skip once the user has started editing so a late-arriving query response can't clobber in-progress edits.
  useEffect(() => {
    if (savedScope && !isDirty) {
      setSelectedByType(groupSelectedByType(savedScope));
      const matchingOption =
        typeof savedScope.hierarchyMatchingOption === 'object'
          ? savedScope.hierarchyMatchingOption?.value
          : savedScope.hierarchyMatchingOption;
      setMatchAll(matchingOption === 'MATCH_ALL');
    }
  }, [savedScope, isDirty]);

  const handleTypeChange = useCallback((typeId: number, ids: number[]) => {
    setIsDirty(true);
    setSelectedByType((previous) => {
      const next = { ...previous };
      if (ids.length > 0) {
        next[typeId] = ids;
      } else {
        delete next[typeId];
      }
      return next;
    });
  }, []);

  const save = useCallback(() => {
    // Skip when the user hasn't touched the scope, so clicking Apply (e.g. for the Display tab) or a
    // pre-hydration Apply never overwrites or clears the saved global hierarchy.
    if (!isDirty) {
      return;
    }

    const hasSelection = Object.values(selectedByType).some((ids) => ids.length > 0);

    // No selection clears the saved scope; otherwise persist the selection.
    const request = hasSelection
      ? updateFilters(buildGlobalHierarchyFiltersBody(selectedByType, matchAll ? 'MATCH_ALL' : 'MATCH_ANY')).unwrap()
      : deleteFilters().unwrap();

    // Clear the dirty flag once the save resolves so reopening the overlay and clicking Apply
    // again is a no-op until the user makes a fresh edit. Don't rely on the refetched scope to
    // reset it: structural sharing keeps the same reference when the scope is unchanged, so the
    // hydrate effect wouldn't fire.
    request.then(() => setIsDirty(false)).catch(() => {});

    toast.promise(request, {
      pending: 'Saving hierarchy filter...',
      success: { render: 'Hierarchy filter saved', autoClose: 1200, type: 'info' },
      error: 'Unable to save hierarchy filter',
    });
  }, [isDirty, selectedByType, matchAll, updateFilters, deleteFilters]);

  useImperativeHandle(ref, () => ({ save }));

  return (
    <div className="flex flex-col gap-2">
      {types.map((type) => (
        <HierarchyTypeMultiSelect
          key={type.id}
          type={type}
          selectedIds={selectedByType[type.id] ?? []}
          onChange={handleTypeChange}
        />
      ))}
      <Checkbox
        name="matchAllHierarchies"
        label="Match all selected hierarchies"
        checked={matchAll}
        onChange={(value) => {
          setIsDirty(true);
          setMatchAll(Boolean(value?.checked));
        }}
      />
    </div>
  );
}

export default HierarchyFilterTab;
